import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HeartPulse,
  Sparkles,
  Star,
  Users,
  ClipboardCheck,
  Target,
  MessageCircleHeart,
  NotebookPen,
  Brain,
  Shield,
  Lock,
  CheckCircle,
  Download,
  FileText,
  TrendingUp,
  ArrowRight,
  Play,
  X
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { apiService } from 'services/api';
import { getPathForPage } from 'utils/navigation';
import { translations, type Language } from 'translations/landing';
import { useThemeLogo } from 'hooks/useThemeLogo';
import { persistence } from 'services/persistence';

type CommunityProgram = {
  programId: string;
  title: string;
  condition?: string;
  summary?: string;
  focusAreas?: string[];
  duration?: string;
  authorEmail?: string;
  publishedAt?: string;
};

interface LandingPageProps {
  language?: Language;
}

export const LandingPage: React.FC<LandingPageProps> = ({ language: propLanguage }) => {
  const navigate = useNavigate();
  const logoSrc = useThemeLogo();
  const [language, setLanguage] = useState<Language>(propLanguage || 'en');

  useEffect(() => {
    if (propLanguage) {
      setLanguage(propLanguage);
    } else {
      const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage;
      setLanguage(browserLang?.startsWith('sv') ? 'sv' : 'en');
    }
  }, [propLanguage]);

  const t = translations[language];
  const [programs, setPrograms] = useState<CommunityProgram[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [programsError, setProgramsError] = useState<string | null>(null);
  const [showSignupModal, setShowSignupModal] = useState(true);
  const [isSignInMode, setIsSignInMode] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupError, setSignupError] = useState<string | null>(null);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let isMounted = true;

    apiService
      .getCommunityPrograms()
      .then((response) => {
        if (!isMounted) return;
        const ordered = [...(response.programs ?? [])]
          .sort((a, b) => {
            const timeA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
            const timeB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
            return timeB - timeA;
          })
          .slice(0, 6);
        setPrograms(ordered);
      })
      .catch((error: Error) => {
        console.error('Failed to load community programs', error);
        if (isMounted) {
          setProgramsError('Community programs are still loading — check back shortly.');
        }
      })
      .finally(() => {
        if (isMounted) setLoadingPrograms(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleOpenApp = useCallback(() => {
    navigate(getPathForPage('ehr'));
  }, [navigate]);

  const handleSignup = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSignupError(null);

      if (!signupEmail || !signupPassword) {
        setSignupError('Please fill in all fields');
        return;
      }

      if (signupPassword.length < 6) {
        setSignupError('Password must be at least 6 characters');
        return;
      }

      setIsSigningUp(true);

      try {
        const profile = persistence.getJSON<Record<string, any>>('eir-user-profile', {});
        const updatedProfile = { ...profile, email: signupEmail };
        persistence.setJSON('eir-user-profile', updatedProfile);
        setShowSignupModal(false);
        setSignupEmail('');
        setSignupPassword('');
      } catch (error) {
        console.error('Signup error:', error);
        setSignupError('Failed to save your information. Please try again.');
      } finally {
        setIsSigningUp(false);
      }
    },
    [signupEmail, signupPassword]
  );

  const handleSignIn = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSignupError(null);

      if (!signupEmail || !signupPassword) {
        setSignupError('Please fill in all fields');
        return;
      }

      setIsSigningUp(true);

      try {
        const profile = persistence.getJSON<Record<string, any>>('eir-user-profile', {});
        if (profile.email && profile.email.toLowerCase() === signupEmail.toLowerCase()) {
          const updatedProfile = { ...profile, email: signupEmail };
          persistence.setJSON('eir-user-profile', updatedProfile);
          setShowSignupModal(false);
          setSignupEmail('');
          setSignupPassword('');
        } else {
          setSignupError('Email not found. Please sign up first or check your email address.');
        }
      } catch (error) {
        console.error('Sign in error:', error);
        setSignupError('Failed to sign in. Please try again.');
      } finally {
        setIsSigningUp(false);
      }
    },
    [signupEmail, signupPassword]
  );

  const formattedPrograms = useMemo(
    () =>
      programs.map((program) => ({
        ...program,
        summary: program.summary || 'A deeply personalized CBT pathway crafted by our community.',
        focusAreas: Array.isArray(program.focusAreas) ? program.focusAreas.slice(0, 3) : [],
      })),
    [programs]
  );

  return (
    <div className="min-h-screen bg-card text-foreground-strong overflow-x-hidden relative">
      <nav className="fixed top-0 left-0 right-0 h-[60px] flex items-center justify-between py-0 px-8 z-[100] bg-card/95 backdrop-blur-sm border-b">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/start')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/start')}
        >
          <img
            className="h-10 w-10 block object-contain"
            src={logoSrc}
            alt="EIR"
            onError={(e) => {
              console.error('Logo failed to load');
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="flex flex-col gap-0.5">
            <div className="text-xl font-bold text-foreground-strong leading-tight">Eir Space</div>
            <div className="text-xs font-medium text-foreground-muted leading-tight">Know your Health</div>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <Button
            variant="outline"
            size="icon"
            className="w-9 h-9 rounded-lg"
            onClick={() => navigate(getPathForPage('journal'))}
            title="Health Journal"
          >
            <NotebookPen size={18} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="w-9 h-9 rounded-lg"
            onClick={() => navigate(getPathForPage('health-assessment'))}
            title="Health Assessment"
          >
            <ClipboardCheck size={18} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="w-9 h-9 rounded-lg"
            onClick={() => navigate(getPathForPage('health-actions'))}
            title="Health Actions"
          >
            <Target size={18} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="w-9 h-9 rounded-lg"
            onClick={() => navigate(getPathForPage('health-programs'))}
            title="Health Programs"
          >
            <Brain size={18} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="w-9 h-9 rounded-lg"
            onClick={() => navigate(getPathForPage('feedback'))}
            title="Feedback Studio"
          >
            <MessageCircleHeart size={18} />
          </Button>
        </div>
      </nav>

      <Dialog open={showSignupModal} onOpenChange={(open) => !open && setShowSignupModal(false)}>
        <DialogContent className="max-w-[450px] w-[90%] bg-card/10 backdrop-blur-sm border border-white/20 p-10">
          <DialogHeader>
            <DialogTitle className="text-[1.75rem] font-bold text-white text-center">
              {isSignInMode ? 'Sign in' : 'Sign up to get started'}
            </DialogTitle>
            <DialogDescription className="text-sm text-white/70 text-center mb-8">
              {isSignInMode
                ? 'Sign in to your account to continue chatting with EIR'
                : 'Create your account to start chatting with EIR'}
            </DialogDescription>
          </DialogHeader>
          <form className="flex flex-col gap-5" onSubmit={isSignInMode ? handleSignIn : handleSignup}>
            {signupError && (
              <div className="py-3 px-4 rounded-xl bg-red-500/20 border-red-500/40 text-red-200 text-sm text-center">
                {signupError}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm text-white/90" htmlFor="signup-email">
                Email
              </label>
              <Input
                id="signup-email"
                type="email"
                placeholder="your@email.com"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                required
                disabled={isSigningUp}
                className="py-4 px-5 rounded-xl border border-white/20 bg-card/10 text-white placeholder:text-white/60"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm text-white/90" htmlFor="signup-password">
                Password
              </label>
              <Input
                id="signup-password"
                type="password"
                placeholder={isSignInMode ? 'Enter your password' : 'At least 6 characters'}
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                required
                disabled={isSigningUp}
                minLength={isSignInMode ? undefined : 6}
                className="py-4 px-5 rounded-xl border border-white/20 bg-card/10 text-white placeholder:text-white/60"
              />
            </div>
            <Button
              type="submit"
              disabled={isSigningUp}
              className="py-4 px-6 rounded-xl font-bold text-base shadow-lg mt-2"
            >
              {isSigningUp
                ? isSignInMode
                  ? 'Signing in...'
                  : 'Creating account...'
                : isSignInMode
                  ? 'Sign in'
                  : 'Sign up'}
            </Button>
          </form>
          <div className="flex gap-2 justify-center mt-6 text-sm text-white/70">
            {isSignInMode ? (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  className="bg-transparent border-none text-primary/80 font-semibold cursor-pointer p-0 underline"
                  onClick={() => {
                    setIsSignInMode(false);
                    setSignupError(null);
                  }}
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  className="bg-transparent border-none text-primary/80 font-semibold cursor-pointer p-0 underline"
                  onClick={() => {
                    setIsSignInMode(true);
                    setSignupError(null);
                  }}
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <section className="min-h-screen pt-[clamp(8rem,12vh,10rem)] px-[clamp(2rem,4vw,3rem)] pb-[clamp(4rem,8vh,6rem)] relative z-[1] flex flex-col gap-12 max-w-[1200px] mx-auto items-center text-center">
        <div className="flex flex-col gap-6">
          <div className="grid gap-6">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary py-3 px-6 rounded-full border-primary/20 backdrop-blur-sm w-fit">
              <Sparkles size={18} />
              {t.hero.badge}
            </span>
            <h1 className="m-0 text-2xl font-bold tracking-tight leading-[1.1] text-foreground-strong">
              {t.hero.title}
            </h1>
            <p className="text-lg leading-relaxed text-foreground-muted m-0 font-normal">
              {t.hero.subtitle}
            </p>
          </div>

          <div className="flex flex-wrap gap-4 mt-6">
            <div className="inline-flex items-center gap-2 py-2.5 px-5 rounded-full bg-primary/5 backdrop-blur-sm border-primary/15 text-primary text-sm font-semibold">
              <Shield size={16} />
              {t.hero.trustBadges.gdpr}
            </div>
            <div className="inline-flex items-center gap-2 py-2.5 px-5 rounded-full bg-primary/5 backdrop-blur-sm border-primary/15 text-primary text-sm font-semibold">
              <Lock size={16} />
              {t.hero.trustBadges.privacy}
            </div>
            <div className="inline-flex items-center gap-2 py-2.5 px-5 rounded-full bg-primary/5 backdrop-blur-sm border-primary/15 text-primary text-sm font-semibold">
              <CheckCircle size={16} />
              {t.hero.trustBadges.zeroStorage}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-8">
            <Button
              className="py-4 px-8 rounded-xl font-bold text-base shadow-lg flex items-center gap-2"
              onClick={handleOpenApp}
            >
              {t.hero.cta.startFree}
              <ArrowRight size={18} />
            </Button>
            <Button
              variant="outline"
              className="py-4 px-8 rounded-xl border border-white/30 bg-card/10 text-white font-semibold text-base"
              onClick={() => setShowSignupModal(true)}
            >
              {t.hero.cta.learnMore}
            </Button>
          </div>

          <div className="flex flex-col gap-4">
            {t.hero.features.map((feature, index) => {
              const icons = [HeartPulse, Sparkles, ClipboardCheck, Target, Star, Users];
              const Icon = icons[index] || HeartPulse;
              return (
                <div className="flex items-start gap-3 text-foreground-muted text-base" key={index}>
                  <Icon size={22} />
                  <span>{feature}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-[clamp(3rem,6vw,5rem)] px-[clamp(2rem,6vw,4rem)] relative z-[3] max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.video.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.video.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.video.subtitle}</p>
          </div>
        </div>
        <div
          className="relative rounded-xl overflow-hidden cursor-pointer bg-primary/20 max-w-[1200px] w-full mt-8 mx-auto"
          onClick={() => setIsVideoModalOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsVideoModalOpen(true);
            }
          }}
          aria-label="Play getting started video"
        >
          <video
            className="w-full h-full block opacity-0"
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            poster="/health_journal_loading.png"
            onLoadedData={() => {
              if (videoRef.current) {
                videoRef.current.classList.add('loaded');
                videoRef.current.play().catch(() => {});
              }
            }}
            onError={() => console.warn('Video failed to load, showing poster image')}
          >
            <source
              src="https://cdn.loom.com/videos/c79b2db5b591446fb4040263dd578d83/mp4-720p-30fps.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 flex items-center justify-center bg-primary/30 z-[1]">
            <div className="w-[100px] h-[100px] rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-primary">
              <Play size={40} fill="currentColor" className="ml-1.5" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-[clamp(5rem,10vw,8rem)] px-[clamp(2rem,6vw,4rem)] relative z-[3] max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.howItWorks.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.howItWorks.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.howItWorks.subtitle}</p>
          </div>
        </div>
        <div className="grid gap-12 mt-16">
          {t.howItWorks.steps.map((step, index) => {
            const icons = [Download, FileText, TrendingUp];
            const Icon = icons[index] || Download;
            return (
              <div className="flex flex-col items-center text-center gap-6 relative" key={index}>
                <div className="w-16 h-16 rounded-full bg-primary/20 border border-primary/20 flex items-center justify-center text-2xl font-bold text-primary shadow-lg">
                  {index + 1}
                </div>
                <div className="w-20 h-20 rounded-xl bg-primary/5 backdrop-blur-sm border-primary/10 flex items-center justify-center text-primary shadow-lg">
                  <Icon size={40} />
                </div>
                <h3 className="m-0 text-lg font-bold text-foreground-strong">{step.title}</h3>
                {index === 0 && (
                  <img
                    className="h-16 w-auto my-4 mx-auto block max-w-full"
                    src="/bankid-logo-small.webp"
                    alt="BankID"
                    onError={() => console.error('BankID logo failed to load')}
                  />
                )}
                <p className="m-0 text-foreground-muted leading-relaxed text-base">{step.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-[clamp(5rem,10vw,8rem)] px-[clamp(2rem,6vw,4rem)] relative z-[3] max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.security.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.security.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.security.subtitle}</p>
          </div>
        </div>
        <div className="grid gap-8 mt-12">
          {t.security.cards.map((card, index) => {
            const icons = [Lock, Shield, CheckCircle];
            const Icon = icons[index] || Lock;
            return (
              <div
                className="bg-card backdrop-blur-sm rounded-xl p-10 border-black/8 grid gap-6"
                key={index}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center text-primary shadow-lg">
                  <Icon size={28} />
                </div>
                <h3 className="m-0 text-xl font-bold text-foreground-strong">{card.title}</h3>
                <p className="m-0 text-foreground-muted leading-relaxed text-base">{card.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-[clamp(5rem,10vw,8rem)] px-[clamp(2rem,6vw,4rem)] relative z-[3] max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.socialProof.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.socialProof.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.socialProof.subtitle}</p>
          </div>
        </div>
        <div className="grid gap-8 mt-12">
          <div className="text-center p-8 bg-card backdrop-blur-sm rounded-xl border-black/8">
            <div className="text-5xl font-black text-primary leading-none mb-2">1000+</div>
            <div className="text-base text-foreground-muted font-medium">{t.socialProof.stats.recordsAnalyzed}</div>
          </div>
          <div className="text-center p-8 bg-card backdrop-blur-sm rounded-xl border-black/8">
            <div className="text-5xl font-black text-primary leading-none mb-2">500+</div>
            <div className="text-base text-foreground-muted font-medium">{t.socialProof.stats.activeUsers}</div>
          </div>
          <div className="text-center p-8 bg-card backdrop-blur-sm rounded-xl border-black/8">
            <div className="text-5xl font-black text-primary leading-none mb-2">99.9%</div>
            <div className="text-base text-foreground-muted font-medium">{t.socialProof.stats.uptime}</div>
          </div>
          <div className="text-center p-8 bg-card backdrop-blur-sm rounded-xl border-black/8">
            <div className="text-5xl font-black text-primary leading-none mb-2">0</div>
            <div className="text-base text-foreground-muted font-medium">{t.socialProof.stats.dataBreaches}</div>
          </div>
        </div>
        <div className="grid gap-8 mt-12">
          {t.socialProof.testimonials.map((testimonial, index) => (
            <div
              className="bg-card backdrop-blur-sm rounded-xl p-8 border-black/8 grid gap-6"
              key={index}
            >
              <p className="m-0 text-foreground-muted leading-loose text-base italic">
                &quot;{testimonial.quote}&quot;
              </p>
              <div className="flex flex-col gap-1">
                <div className="font-bold text-foreground-strong text-base">{testimonial.name}</div>
                <div className="text-sm text-foreground-muted">{testimonial.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {isVideoModalOpen && (
        <div
          className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-black/60"
          onClick={(e) => e.target === e.currentTarget && setIsVideoModalOpen(false)}
        >
          <div className="relative w-full max-w-[1400px] bg-black rounded-xl overflow-hidden">
            <Button
              variant="outline"
              size="icon"
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/15 backdrop-blur-sm border-white/20 text-white z-[10] hover:bg-card/25"
              onClick={() => setIsVideoModalOpen(false)}
            >
              <X size={20} />
            </Button>
            <iframe
              className="w-full aspect-video border-none block"
              src="https://www.loom.com/embed/c79b2db5b591446fb4040263dd578d83?autoplay=true&muted=false"
              allowFullScreen
              title="Getting Started with EIR Space"
              allow="autoplay; fullscreen; microphone; camera"
            />
          </div>
        </div>
      )}

      <section className="grid gap-8 py-[clamp(4rem,8vw,6rem)] px-[clamp(2rem,6vw,4rem)] relative z-[3] max-w-[1400px] mx-auto">
        {t.highlights.map((item, index) => {
          const icons = [Download, Sparkles, HeartPulse, Shield];
          const Icon = icons[index] || Download;
          return (
            <div
              className="bg-card backdrop-blur-sm rounded-xl p-10 border-black/8 grid gap-5"
              key={item.title}
            >
              <span className="inline-flex w-14 h-14 items-center justify-center rounded-xl bg-primary/20 text-primary shadow-lg border-primary/10">
                <Icon size={20} />
              </span>
              <h3 className="m-0 text-xl font-bold text-foreground-strong">{item.title}</h3>
              <p className="m-0 text-foreground-muted leading-relaxed">{item.description}</p>
            </div>
          );
        })}
      </section>

      <section className="py-[clamp(4rem,8vw,6rem)] px-[clamp(2rem,6vw,4rem)] grid gap-10 relative z-[3] max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.features.assessment.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.features.assessment.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.features.assessment.subtitle}</p>
          </div>
          <Button
            variant="outline"
            className="py-4 px-8 rounded-xl border border-primary/30 bg-primary/5 text-primary font-semibold"
            onClick={() => navigate(getPathForPage('health-assessment'))}
          >
            {t.features.assessment.cta}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="flex flex-col gap-6">
            <p className="m-0 text-foreground-muted leading-relaxed">
              Structured health assessments help you understand your mental and physical wellbeing today.
              Each assessment follows evidence-based screening flows designed for Swedish healthcare pathways.
            </p>
            <ul className="m-0 p-0 list-disc pl-6 space-y-2 text-foreground-muted">
              <li>Complete multiple assessments to build a comprehensive picture of your health</li>
              <li>Save results to compare progress over time and share with healthcare professionals</li>
              <li>Unlock personalized AI assessment summaries after completing 5 assessments</li>
              <li>Get recommended health actions based on your assessment results</li>
            </ul>
          </div>
          <div className="bg-muted backdrop-blur-sm rounded-xl p-10 border-black/8 flex flex-col items-center justify-center gap-6 min-h-[300px]">
            <div className="w-20 h-20 rounded-xl bg-primary/20 flex items-center justify-center text-primary shadow-lg">
              <ClipboardCheck size={40} />
            </div>
            <p className="text-center text-foreground-muted text-lg m-0">
              Track your health journey with structured assessments
            </p>
          </div>
        </div>
      </section>

      <section className="py-[clamp(4rem,8vw,6rem)] px-[clamp(2rem,6vw,4rem)] grid gap-10 relative z-[3] max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.features.actions.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.features.actions.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.features.actions.subtitle}</p>
          </div>
          <Button
            variant="outline"
            className="py-4 px-8 rounded-xl border border-primary/30 bg-primary/5 text-primary font-semibold"
            onClick={() => navigate(getPathForPage('health-actions'))}
          >
            {t.features.actions.cta}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="bg-muted backdrop-blur-sm rounded-xl p-10 border-black/8 flex flex-col items-center justify-center gap-6 min-h-[300px] order-2 md:order-1">
            <div className="w-20 h-20 rounded-xl bg-primary/20 flex items-center justify-center text-primary shadow-lg">
              <Target size={40} />
            </div>
            <p className="text-center text-foreground-muted text-lg m-0">
              AI-generated suggestions prioritized by impact and effort
            </p>
          </div>
          <div className="flex flex-col gap-6 order-1 md:order-2">
            <p className="m-0 text-foreground-muted leading-relaxed">
              Recommended Health Actions translate assessments and records into concrete next steps. Review
              AI-generated suggestions and stay accountable to your care plan.
            </p>
            <ul className="m-0 p-0 list-disc pl-6 space-y-2 text-foreground-muted">
              <li>Search for specific actions like &quot;stretching for back pain&quot; or &quot;breathing exercises for stress&quot;</li>
              <li>Generate actions automatically from your health journal entries</li>
              <li>Track active and completed actions to monitor your progress</li>
              <li>Each action is designed to take 15 minutes or less to complete</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-[clamp(4rem,8vw,6rem)] px-[clamp(2rem,6vw,4rem)] grid gap-10 relative z-[3] max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.features.feedback.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.features.feedback.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.features.feedback.subtitle}</p>
          </div>
          <Button
            variant="outline"
            className="py-4 px-8 rounded-xl border border-primary/30 bg-primary/5 text-primary font-semibold"
            onClick={() => navigate(getPathForPage('feedback'))}
          >
            {t.features.feedback.cta}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="flex flex-col gap-6">
            <p className="m-0 text-foreground-muted leading-relaxed">
              Feedback Studio lets you invite others to share their perspective on your health journey in a
              supportive way. Every insight is transformed into inspiring language that you can act on at your
              own pace.
            </p>
            <ul className="m-0 p-0 list-disc pl-6 space-y-2 text-foreground-muted">
              <li>Create and share feedback forms with friends, family, or care partners</li>
              <li>AI automatically transforms responses into clear insights and actionable steps</li>
              <li>All negativity is filtered out—you only receive constructive guidance</li>
              <li>Focus on how you can improve and grow with gentle, supportive feedback</li>
            </ul>
          </div>
          <div className="bg-muted backdrop-blur-sm rounded-xl p-10 border-black/8 flex flex-col items-center justify-center gap-6 min-h-[300px]">
            <div className="w-20 h-20 rounded-xl bg-primary/20 flex items-center justify-center text-primary shadow-lg">
              <MessageCircleHeart size={40} />
            </div>
            <p className="text-center text-foreground-muted text-lg m-0">
              Collect heartfelt reflections that celebrate your strengths
            </p>
          </div>
        </div>
      </section>

      <section className="py-[clamp(4rem,10vw,7rem)] px-[clamp(2rem,6vw,4rem)] grid gap-12 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="grid gap-4 max-w-[700px]">
            <span className="font-bold text-primary tracking-widest uppercase text-sm">
              {t.features.community.eyebrow}
            </span>
            <h2 className="m-0 text-xl font-bold tracking-tight text-foreground-strong">
              {t.features.community.title}
            </h2>
            <p className="m-0 text-lg leading-loose text-foreground-muted">{t.features.community.subtitle}</p>
          </div>
          <Button
            variant="outline"
            className="py-4 px-8 rounded-xl border border-primary/30 bg-primary/5 text-primary font-semibold"
            onClick={() => navigate(getPathForPage('health-programs'))}
          >
            {t.features.community.cta}
          </Button>
        </div>

        {loadingPrograms ? (
          <div className="grid gap-8">
            {Array.from({ length: 4 }).map((_, index) => (
              <div className="h-[200px] rounded-xl bg-muted animate-pulse" key={index} />
            ))}
          </div>
        ) : programsError ? (
          <div className="bg-muted backdrop-blur-sm rounded-xl py-12 px-8 border text-center text-foreground-muted">
            {programsError}
          </div>
        ) : formattedPrograms.length === 0 ? (
          <div className="bg-muted backdrop-blur-sm rounded-xl py-12 px-8 border text-center text-foreground-muted">
            No community programs yet — be the first to publish one that supports others.
          </div>
        ) : (
          <div className="grid gap-8">
            {formattedPrograms.map((program) => (
              <div
                className="bg-card backdrop-blur-sm rounded-xl p-8 border-black/8 grid gap-4"
                key={program.programId}
              >
                <div className="flex items-center gap-2 flex-wrap text-sm text-foreground-muted">
                  {program.condition && <span>{program.condition}</span>}
                  {program.duration && <span>• {program.duration}</span>}
                </div>
                <h3 className="m-0 text-xl font-bold text-foreground-strong">{program.title}</h3>
                <p className="m-0 text-foreground-muted">{program.summary}</p>
                <div className="flex items-center gap-2 flex-wrap text-sm text-foreground-muted">
                  {program.focusAreas?.map((area) => (
                    <span
                      className="inline-flex items-center py-1.5 px-3.5 rounded-full bg-primary/10 text-primary text-xs font-semibold border-primary/20"
                      key={area}
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="py-[clamp(4rem,10vw,7rem)] px-[clamp(2rem,6vw,4rem)] grid gap-8 max-w-[1400px] mx-auto">
        <div className="bg-primary/10 backdrop-blur-sm rounded-xl p-6 text-foreground-strong relative overflow-hidden grid gap-8 border-primary/10 text-center items-center">
          <h3 className="m-0 text-lg font-semibold tracking-tight relative z-[1] text-foreground-strong">
            {t.invitation.title}
          </h3>
          <p className="relative z-[1] text-base max-w-[700px] m-0 text-foreground">
            {t.invitation.subtitle}
          </p>
          <div className="flex items-center justify-center gap-5 flex-wrap relative z-[1]">
            <Button className="py-3 px-7 rounded-full font-semibold text-sm" onClick={handleOpenApp}>
              {t.invitation.cta.startFree}
            </Button>
            <Button
              variant="outline"
              className="py-4 px-8 rounded-xl border border-primary/30 bg-primary/5 text-primary font-semibold"
              onClick={() => navigate(`${getPathForPage('eir-docs')}?autoOpen=true`)}
            >
              {t.invitation.cta.learnMore}
            </Button>
          </div>
        </div>
      </section>

      <div className="py-[clamp(3rem,7vw,4rem)] px-[clamp(2rem,6vw,4rem)] flex flex-col items-center gap-7 text-foreground-muted text-center border-t max-w-[1400px] mx-auto bg-muted">
        <div className="flex items-center gap-2 font-semibold text-foreground-muted text-base">
          <span>❤️</span>
          {t.footer.crafted}
        </div>
        <div className="flex items-center justify-center flex-wrap gap-8 text-sm">
          <button
            className="border-none bg-transparent text-inherit cursor-pointer p-0 hover:text-foreground"
            onClick={() => navigate(getPathForPage('eir-docs'))}
          >
            {t.footer.links.docs}
          </button>
          <button
            className="border-none bg-transparent text-inherit cursor-pointer p-0 hover:text-foreground"
            onClick={() => navigate(getPathForPage('health-literacy'))}
          >
            {t.footer.links.healthLiteracy}
          </button>
          <button
            className="border-none bg-transparent text-inherit cursor-pointer p-0 hover:text-foreground"
            onClick={() => navigate(getPathForPage('feedback'))}
          >
            {t.footer.links.feedback}
          </button>
          <button
            className="border-none bg-transparent text-inherit cursor-pointer p-0 hover:text-foreground"
            onClick={() => navigate(getPathForPage('request-care'))}
          >
            {t.footer.links.requestCare}
          </button>
          <button
            className="border-none bg-transparent text-inherit cursor-pointer p-0 hover:text-foreground"
            onClick={() => navigate(getPathForPage('privacy'))}
          >
            {t.footer.links.privacy}
          </button>
          <button
            className="border-none bg-transparent text-inherit cursor-pointer p-0 hover:text-foreground"
            onClick={() => window.open('https://www.1177.se', '_blank')}
          >
            {t.footer.links.se1177}
          </button>
        </div>
        <p className="m-0 text-sm max-w-[600px]">{t.footer.caption}</p>
      </div>
    </div>
  );
};
